import api from "./api";

const create = (data) => {
  return api.post("/api/wo-form", data);
};

const findAll = (woId) => {
  return api.get("/api/wo-form", {
    params: {
      woId,
    },
  });
};

const findOne = (id) => {
  return api.get(`/api/wo-form/${id}`);
};

const update = (id, data) => {
  return api.patch(`/api/wo-form/${id}`, data);
};

const remove = (id) => {
  return api.delete(`/api/wo-form/${id}`);
};

// Dedicated staff endpoints so form:duplicate / form:assign / form:unassign /
// form:update are each guarded separately from the generic (ungated) update
// that the technician app uses to fill and submit forms.
const duplicate = (id, createdBy) => {
  return api.post(`/api/wo-form/${id}/duplicate`, { createdBy });
};

const assign = (id, woId) => {
  return api.post(`/api/wo-form/${id}/assign`, { woId });
};

const unassign = (id) => {
  return api.post(`/api/wo-form/${id}/unassign`);
};

const updateDetails = (id, data) => {
  return api.patch(`/api/wo-form/${id}/details`, data);
};

// Send a submitted form back to Partially Filled so the technicians can finish
// and resubmit it. Gated with form:reopen.
const reopen = (id) => {
  return api.post(`/api/wo-form/${id}/reopen`);
};

// Whether the customer sees this form on the service ticket. Goes through the
// form:update-gated details endpoint — it is an office decision, and the generic
// PATCH the technician app uses strips it.
const setIncludeInServiceTicket = (id, includeInServiceTicket) => {
  return api.patch(`/api/wo-form/${id}/details`, { includeInServiceTicket });
};

// Create a form instance seeded from a template. Gated with form:create (same
// as a blank create) — it just hits a separate endpoint that copies the
// template's schema.
const createFromTemplate = (data) => {
  return api.post("/api/wo-form/from-template", data);
};

const getAllForms = (filters = {}) => {
  const params = {};
  if (filters.status !== undefined && filters.status !== "") params.status = filters.status;
  if (filters.formType !== undefined && filters.formType !== "") params.formType = filters.formType;
  if (filters.frequency !== undefined && filters.frequency !== "") params.frequency = filters.frequency;
  if (filters.branchId !== undefined && filters.branchId !== "") params.branchId = filters.branchId;
  if (filters.woNumber !== undefined && filters.woNumber !== "") params.woNumber = filters.woNumber;
  if (filters.unassigned) params.unassigned = "true";
  if (filters.customerId) params.customerId = filters.customerId;
  // Search matches form name, type, WO number, branch and technicians
  // server-side; frequencyMatches carries the frequency values the keyword
  // names, since frequency labels are still client-side.
  if (filters.keyword) params.keyword = filters.keyword;
  if (filters.frequencyMatches) params.frequencyMatches = filters.frequencyMatches;
  return api.get("/api/wo-form/all-forms", { params });
};

// Location-scoped forms for the customer:read-gated Location detail. Uses a
// customer:read-gated endpoint so viewing a location doesn't require form:read.
const getAllFormsForLocation = (customerId) => {
  return api.get("/api/wo-form/all-forms/for-location", { params: { customerId } });
};

// The Standard Forms library as it applies to one work order: which library
// templates have copies on it, and whether a new copy may be started. Auth-only
// on the backend — technicians hold no staff form:* permissions.
const getStandardLibrary = (woId) => {
  return api.get("/api/wo-form/standard", { params: { woId } });
};

// Open a Standard Form on a work order. Creates the row on first use, or returns
// the copy already open — a second copy is only reachable once the previous one
// is submitted.
const startStandardForm = (templateId, woId) => {
  return api.post(`/api/wo-form/standard/${templateId}/start`, { woId });
};

// Aliases for consistency with existing service naming style.
const save = (data) => create(data);
const getAll = (woId) => findAll(woId);
const getById = (id) => findOne(id);
const deleteById = (id) => remove(id);

const WOFormService = {
  create,
  findAll,
  findOne,
  update,
  remove,
  duplicate,
  assign,
  unassign,
  reopen,
  updateDetails,
  setIncludeInServiceTicket,
  createFromTemplate,
  getStandardLibrary,
  startStandardForm,
  save,
  getAll,
  getAllForms,
  getAllFormsForLocation,
  getById,
  deleteById,
};

export default WOFormService;
